import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';

import { AddIcon, RepeatIcon } from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';

import { apiGetProducerCodes } from 'api';

import type {
  APIPaginatedResponse,
  APIProducerCodeData,
} from 'types/api.types';

const TABLE_HEADERS = ['Name', 'Active', 'Created At', 'Updated At'];

const ProducerCodeListView = () => {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['producer-code-list', page],
    queryFn: () => apiGetProducerCodes(page + 1, ''),
  }) as {
    data: APIPaginatedResponse<APIProducerCodeData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const handleEditProducerCode = (producerCodeData: APIProducerCodeData) => {
    navigate(`/admin/settings/producer_codes/edit/${producerCodeData.id}`);
  };

  const handleCreateProducerCode = () => {
    navigate('/admin/settings/producer_codes/new');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Producer Codes
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh producer codes" hasArrow>
            <IconButton
              aria-label="Refresh producer codes"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={handleCreateProducerCode}
          >
            New Producer Code
          </Button>
        </Flex>
      </Flex>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading producer codes...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((producerCode) => (
            <Tr
              key={producerCode.id}
              cursor="pointer"
              _hover={{
                bg: 'gray.200',
              }}
              onClick={() => handleEditProducerCode(producerCode)}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {producerCode.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {producerCode.is_active ? 'Yes' : 'No'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {formatDate(producerCode.created_at)}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {formatDate(producerCode.updated_at)}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No producer codes found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default ProducerCodeListView;
